// Session-gated license reads for the commerce screens (US-140..142).
//
// `GET /licenses` and `GET /licenses/usage` are cookie-authenticated
// (`require_session` — the human credential, `backend/auth/session.py`), so
// this is deliberately NOT `src/dashboard/api.ts`'s pattern: that module
// sends `X-mAiSight-Key` for the embed's machine credential. Here the
// browser's HttpOnly `maisight_session` cookie carries identity, so every
// request goes with `credentials: "same-origin"` and no key header at all.
//
// `same-origin`, NOT `include`, and the difference is not cosmetic — this
// matches `screens/overview/api.ts`, which had it right first. The API sets
// `allow_credentials=False` (`backend/api/app.py`), so a cross-origin fetch
// carrying `include` is rejected by the browser before the response is read and
// every panel goes dark; `same-origin` simply omits the cookie and the screen
// renders its signed-out state. Deployed (and under `scripts/gate-demo.sh`) the
// SPA reaches the API same-origin, so the session cookie rides as intended.
//
// Response shapes are pinned to setup-backend's Wave 1 handoff
// (PRDs/18-figma-surfaces/progress/progress-setup-backend.txt) verbatim.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

export type ResolvedLicenseState = "trial" | "active" | "expired" | "unknown";
export type Plan = "trial" | "pro" | "scale" | "enterprise" | null;

export interface LicenseRecord {
  org_slug: string;
  plan: Plan;
  domains: string[];
  status: "active" | "canceled" | null;
  contact_email: string | null;
  resolved_state: ResolvedLicenseState;
  /** Only meaningful when resolved_state === "trial". */
  days_remaining: number | null;
  /** Naive-UTC, no offset/Z suffix. Null for a paid plan or no license row. */
  expires_at: string | null;
  created_at: string | null;
}

export interface LicenseUsage {
  org_slug: string;
  window_days: number;
  document_count: number;
  term_count: number;
  /** Null iff cost_unknown is true — never fabricate a number here. */
  total_cost_usd: number | null;
  cost_unknown: boolean;
  try_spend_today_usd: number;
  try_daily_cap_usd: number;
}

function endpoint(path: string): string {
  return new URL(
    `${API_BASE}${path}`,
    typeof window !== "undefined" ? window.location.origin : undefined,
  ).toString();
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(endpoint(path), {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`GET ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useLicense(): UseQueryResult<LicenseRecord, Error> {
  return useQuery<LicenseRecord, Error>({
    queryKey: ["license"],
    queryFn: () => getJSON<LicenseRecord>("/licenses"),
  });
}

export function useLicenseUsage(
  days = 30,
): UseQueryResult<LicenseUsage, Error> {
  return useQuery<LicenseUsage, Error>({
    queryKey: ["license-usage", days],
    queryFn: () => getJSON<LicenseUsage>(`/licenses/usage?days=${days}`),
  });
}

/** True when the failure is "not signed in" rather than a real outage — the
 *  two screens read differently: one asks for a session, the other apologizes. */
export function isUnauthorized(error: Error | null): boolean {
  return error?.message.includes(" 401") ?? false;
}
